import { Link, createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft } from 'lucide-react'

import { CarrierStatusBadge } from '@/components/carrier/carrier-status-badge'
import { VtpWaybillLinkDialog } from '@/components/carrier/vtp-waybill-link-dialog'
import {
    VtpShipmentGoodsSection,
    VtpShipmentInfoSection,
    VtpShipmentJourneySection,
    VtpShipmentMoneySection,
    VtpShipmentNoteSection,
    VtpShipmentPartiesSections,
} from '@/components/carrier/vtp-shipment-detail-sections'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useVtpShipmentDetailTracking } from '@/hooks/use-vtp-shipment-tracking'
import { useVtpShipment } from '@/hooks/use-vtp-shipments-query'
import { isWaybillLinkable } from '@/lib/carrier-status'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/logistics/vtp-shipments/$trackingCode',
)({
    // `from` chỉ để đo người dùng tới trang từ đâu (danh sách / chi tiết đơn).
    validateSearch: (search: Record<string, unknown>): VtpDetailSearch => ({
        from:
            search.from === 'list' || search.from === 'order'
                ? search.from
                : undefined,
    }),
    component: VtpShipmentDetailPage,
})

type VtpDetailSearch = { from?: 'list' | 'order' }

function VtpShipmentDetailPage() {
    const { trackingCode } = Route.useParams()
    const { hasPermission } = useAuth()
    const { from } = Route.useSearch()
    // Gắn vận đơn là đường ghi như đẩy vận đơn từ ERP: cùng cờ và cùng quyền.
    const carrierWrites = Boolean(useFeatureFlag('dev-carrier-shipping'))
    const {
        data: shipment,
        isLoading,
        isError,
        status,
        refetch,
    } = useVtpShipment(trackingCode)
    useVtpShipmentDetailTracking({
        trackingCode,
        state: status,
        found: Boolean(shipment),
        hasInternalOrder: Boolean(shipment?.order_id),
        entry: from ?? 'direct',
    })

    if (!hasPermission('sales_orders.read')) {
        return (
            <div className="p-8 text-center text-destructive">
                {m.common_permission_denied()}
            </div>
        )
    }
    if (isError) {
        return (
            <div
                role="alert"
                className="page-pad flex items-center gap-3 text-sm text-destructive"
            >
                <span>{m.carrier_tracking_error()}</span>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void refetch()}
                >
                    {m.carrier_tracking_retry()}
                </Button>
            </div>
        )
    }
    if (isLoading || !shipment) {
        return (
            <div className="page-pad text-sm text-muted-foreground">
                {isLoading
                    ? m.vtp_shipment_loading()
                    : m.vtp_shipment_not_found()}
            </div>
        )
    }

    const canLink =
        carrierWrites &&
        hasPermission('carriers.dispatch') &&
        isWaybillLinkable(shipment)

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            <div className="flex flex-wrap items-start gap-3">
                <PageBackButton asChild>
                    <Link to="/logistics/vtp-shipments">
                        <ArrowLeft className="size-4" />
                    </Link>
                </PageBackButton>
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-semibold">
                        {m.vtp_shipment_title({ code: shipment.tracking_code })}
                    </h1>
                    <CarrierStatusBadge
                        shipment={{
                            status: 'in_transit',
                            carrier_status_code: shipment.carrier_status_code,
                        }}
                    />
                    {!canLink && (
                        <Badge variant="outline">
                            {m.vtp_shipment_view_only()}
                        </Badge>
                    )}
                </div>
                {canLink && (
                    <div className="sm:ml-auto">
                        <VtpWaybillLinkDialog
                            trackingCode={shipment.tracking_code}
                        />
                    </div>
                )}
            </div>

            {!shipment.order_id && (
                <div
                    role="note"
                    className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
                >
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
                    <div>
                        <div className="font-medium">
                            {m.vtp_shipments_no_internal_order()}
                        </div>
                        <div className="text-muted-foreground">
                            {m.vtp_shipment_no_internal_hint()}
                        </div>
                    </div>
                </div>
            )}

            <div className="grid gap-5 lg:grid-cols-3 lg:items-start">
                <div className="order-2 space-y-5 lg:order-none">
                    <VtpShipmentInfoSection shipment={shipment} />
                    <VtpShipmentPartiesSections shipment={shipment} />
                </div>
                <div className="order-3 space-y-5 lg:order-none">
                    <VtpShipmentGoodsSection shipment={shipment} />
                    <VtpShipmentMoneySection shipment={shipment} />
                    <VtpShipmentNoteSection shipment={shipment} />
                </div>
                <div className="order-1 lg:order-none">
                    <VtpShipmentJourneySection shipment={shipment} />
                </div>
            </div>
        </div>
    )
}
